/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  onMounted,
  watch,
  onUnmounted,
} from "vue";
import { useShop } from "@app/vue/providers/ShopConnection";
import type { Reservation, ReservationRequest } from "@lemmonhq/types";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AcceptReservationRequest } from "./AcceptReservationRequest";
import { ReservationRequestDetails } from "./ReservationRequestDetails";
import { ReservationDetails } from "../ReservationsSchedule/components/ReservationDetails";
import { CreateReservationForm } from "../ReservationsSchedule/components/CreateReservationForm";
import { PageHeader } from "./components/PageHeader";
import { LoadingState } from "./components/LoadingState";
import { ErrorState } from "./components/ErrorState";
import { EmptyState } from "./components/EmptyState";
import { ContentList } from "./components/ContentList";
import { BatchActions } from "./components/BatchActions";
import { ScheduledClosuresSheet } from "./ScheduledClosuresSheet";
import type { TabValue } from "./config";
import { TAB_CONFIG } from "./config";
import { useReservationRequestsQuery } from "./composables/useReservationRequestsQuery";
import { useReservationsQuery } from "./composables/useReservationsQuery";
import { useReservationRequestPageState } from "./composables/useReservationRequestPageState";
import { useRejectRequestMutation } from "./composables/useReservationRequestMutations";
import { useReservationRequestBatchActions } from "./composables/useReservationRequestBatchActions";
import { useAutoConfirmSetting } from "./composables/useAutoConfirmSetting";
import { usePauseReservations } from "./composables/usePauseReservations";
import { useTabCounts } from "./composables/useTabCounts";
import { useSheetState } from "./composables/useSheetState";

export const ReservationTabsPage = defineComponent({
  name: "ReservationTabsPage",
  setup() {
    const shop = useShop();

    const {
      restaurantTimeZone,
      reservationSchedule,
      activeTab,
      isSearchOpen,
      searchQuery,
      selectedDate,
      viewMode,
      isRequestTab,
      requestFilters,
      reservationFilters,
      handleTabChange,
      handleDateChange,
      handleClearDate,
      handleSearchToggle,
      handleSearchChange,
      handleViewModeChange,
    } = useReservationRequestPageState();

    const scrollContainerRef = ref<HTMLElement | null>(null);

    // Declare useTabCounts first so refreshTabCounts can be passed into mutations
    const { tabCounts, refreshTabCounts } = useTabCounts({
      selectedDate,
      viewMode,
      restaurantTimeZone,
      reservationSchedule,
      activeTab,
    });

    const rejectMutation = useRejectRequestMutation({
      onSuccess: () => void refreshTabCounts(),
    });

    const requestsQuery = useReservationRequestsQuery({
      filters: requestFilters,
      enabled: isRequestTab,
    });
    const reservationsQuery = useReservationsQuery({
      filters: reservationFilters,
      enabled: computed(() => !isRequestTab.value),
    });

    const {
      selectedIds: selectedRequestIds,
      batchLoading,
      toggleSelect: toggleRequestSelection,
      selectAll: selectAllRequests,
      clearSelection,
      batchAccept: handleBatchAccept,
      batchReject: handleBatchReject,
    } = useReservationRequestBatchActions({
      allRequests: requestsQuery.pages,
      onAfterMutation: () => void refreshTabCounts(),
    });

    const { autoConfirmEnabled, autoConfirmLoading, handleToggleAutoConfirm } =
      useAutoConfirmSetting();
    const { reservationsPaused, reservationsPauseLoading, handleTogglePause } =
      usePauseReservations();

    const sheets = useSheetState({
      rejectMutate: rejectMutation.mutate,
      reservationPages: reservationsQuery.pages,
      onAfterMutation: () => void refreshTabCounts(),
    });

    // Derived list state
    const config = computed(() => TAB_CONFIG[activeTab.value]);
    const isRequestView = computed(() => config.value.type === "request");
    const sortedReservations = computed(() =>
      [...reservationsQuery.pages.value].sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
      ),
    );
    const hasData = computed(() =>
      isRequestView.value
        ? requestsQuery.pages.value.length > 0
        : sortedReservations.value.length > 0,
    );
    const isLoading = computed(() =>
      isRequestView.value
        ? requestsQuery.isLoading.value
        : reservationsQuery.isLoading.value,
    );
    const loadingMore = computed(() =>
      isRequestView.value
        ? requestsQuery.isFetchingNextPage.value
        : reservationsQuery.isFetchingNextPage.value,
    );
    const queryError = computed(() =>
      isRequestView.value
        ? requestsQuery.error.value
        : reservationsQuery.error.value,
    );

    const handleLoadMore = () => {
      if (isRequestView.value) {
        if (requestsQuery.hasNextPage.value) requestsQuery.fetchNextPage();
      } else {
        if (reservationsQuery.hasNextPage.value)
          reservationsQuery.fetchNextPage();
      }
    };

    const handleTabChangeWithClear = (tab: TabValue) => {
      handleTabChange(tab);
      clearSelection();
    };

    // Infinite scroll
    const handleScroll = () => {
      const container = scrollContainerRef.value;
      if (!container || loadingMore.value) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 200) handleLoadMore();
    };

    let searchTimeout: ReturnType<typeof setTimeout>;
    watch(searchQuery, () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        /* filters are reactive — Vue Query picks up the change */
      }, 300);
    });

    onMounted(() => {
      const container = scrollContainerRef.value;
      if (container) container.addEventListener("scroll", handleScroll);
    });
    onUnmounted(() => {
      clearTimeout(searchTimeout);
      const container = scrollContainerRef.value;
      if (container) container.removeEventListener("scroll", handleScroll);
    });

    return () => (
      <div class="flex flex-1 flex-col overflow-x-hidden">
        <PageHeader
          pendingCount={shop.pendingReservationRequestsCount.value}
          tabCounts={tabCounts.value}
          activeTab={activeTab.value}
          selectedDate={selectedDate.value}
          loading={isLoading.value}
          isSearchOpen={isSearchOpen.value}
          searchQuery={searchQuery.value}
          autoConfirmEnabled={autoConfirmEnabled.value}
          autoConfirmLoading={autoConfirmLoading.value}
          onTabChange={handleTabChangeWithClear}
          onDateChange={handleDateChange}
          onClearDate={handleClearDate}
          onRefresh={() => {
            requestsQuery.fetchNextPage();
            reservationsQuery.fetchNextPage();
          }}
          onSearchToggle={handleSearchToggle}
          onSearchChange={handleSearchChange}
          onCreateReservation={() =>
            (sheets.showCreateReservationForm.value = true)
          }
          onToggleAutoConfirm={handleToggleAutoConfirm}
          viewMode={viewMode.value}
          onViewModeChange={handleViewModeChange}
          reservationsPaused={reservationsPaused.value}
          reservationsPauseLoading={reservationsPauseLoading.value}
          onTogglePause={handleTogglePause}
          onOpenScheduledClosures={() =>
            (sheets.showScheduledClosures.value = true)
          }
        />

        <AppSubpage>
          <AppSubpageContent>
            <div
              ref={scrollContainerRef}
              class="h-full overflow-y-auto overflow-x-hidden "
            >
              {isLoading.value ? (
                <LoadingState tab={activeTab.value} />
              ) : queryError.value ? (
                <ErrorState
                  tab={activeTab.value}
                  error={String(queryError.value)}
                  onRetry={handleLoadMore}
                />
              ) : !hasData.value ? (
                <EmptyState
                  tab={activeTab.value}
                  hasDateFilter={!!selectedDate.value}
                  hasSearchQuery={!!searchQuery.value}
                />
              ) : (
                <>
                  {isRequestView.value && selectedRequestIds.value.size > 0 && (
                    <BatchActions
                      selectedCount={selectedRequestIds.value.size}
                      totalCount={requestsQuery.pages.value.length}
                      onSelectAll={selectAllRequests}
                      onClearSelection={clearSelection}
                      onAcceptSelected={handleBatchAccept}
                      onDeclineSelected={handleBatchReject}
                      loading={batchLoading.value}
                    />
                  )}
                  <ContentList
                    requests={requestsQuery.pages.value}
                    reservations={sortedReservations.value}
                    isRequestView={isRequestView.value}
                    loadingMore={loadingMore.value}
                    restaurantTimeZone={restaurantTimeZone.value}
                    onRequestClick={sheets.handleRequestClick}
                    onRequestAccept={sheets.handleRequestAccept}
                    onRequestReject={sheets.handleRequestReject}
                    onReservationClick={sheets.handleReservationClick}
                    selectedIds={selectedRequestIds.value}
                    onToggleSelect={toggleRequestSelection}
                    viewMode={viewMode.value}
                  />
                </>
              )}
            </div>
          </AppSubpageContent>

          {sheets.selectedRequest.value && (
            <AcceptReservationRequest
              request={sheets.selectedRequest.value}
              isOpen={sheets.showAcceptSheet.value}
              onClose={sheets.closeAcceptSheet}
              onAccept={() => {}}
              onReject={() => {}}
              onIgnore={sheets.handleIgnore}
              handleActionComplete={sheets.handleActionComplete}
            />
          )}

          {sheets.showRequestDetailsSheet.value &&
            sheets.selectedRequest.value && (
              <ReservationRequestDetails
                request={sheets.selectedRequest.value}
                linkedReservation={sheets.linkedReservation()}
                isOpen={sheets.showRequestDetailsSheet.value}
                onClose={() => (sheets.showRequestDetailsSheet.value = false)}
                onAcceptIntent={sheets.handleAcceptIntent}
                onRejectIntent={sheets.handleRejectIntent}
                onCancelIntent={sheets.handleCancelIntent}
              />
            )}

          {sheets.showReservationDetailsSheet.value &&
            sheets.selectedReservation.value && (
              <ReservationDetails
                reservation={sheets.selectedReservation.value}
                onClose={() =>
                  (sheets.showReservationDetailsSheet.value = false)
                }
                onUpdate={sheets.handleReservationUpdate}
                onReservationCancel={() => {}}
              />
            )}

          {sheets.showCreateReservationForm.value && (
            <CreateReservationForm
              onSubmit={sheets.handleCreateReservation}
              onCancel={() => (sheets.showCreateReservationForm.value = false)}
            />
          )}
          {sheets.showScheduledClosures.value ? (
            <ScheduledClosuresSheet
              isOpen={sheets.showScheduledClosures.value}
              onClose={() => (sheets.showScheduledClosures.value = false)}
            />
          ) : null}
        </AppSubpage>
      </div>
    );
  },
});
